import AddIcon from "@mui/icons-material/Add";
import CachedIcon from "@mui/icons-material/Cached";
import {
  Alert,
  Box,
  Chip,
  Container,
  Fab,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import ImportStatusChip from "./ImportStatusChip";
import ImportsResultsCell from "./ImportsResultsCell";
import SelectEntity from "components/SelectEntity";
import { isValid } from "date-fns";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  AdminListRequestOfJobSummaryFilter,
  IImportJobSummary,
  ImportJobSummary,
  JobSummaryFilter,
  JobTypeFilter,
  ListRequestOfBaseFilter,
  ListRequestOfJobTypeFilter,
  ListResponseOfIdNameModel,
  ListResponseOfImportJobStatus,
  ListResponseOfImportJobSummary,
  ListResponseOfImportJobType,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { JobStatus } from "types/common-types";
import { standardDateFormat } from "utils/dateUtils";

interface IImportJobSummaryWithId extends IImportJobSummary, LazyTableRow {
  id: string;
}

const POLL_INTERVAL_MS = 30000;
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

const styles = {
  quickFilters: {
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
  },
};

const isInProgress = (job: ImportJobSummary) =>
  job.jobStatusName === JobStatus.Processing ||
  job.jobStatusName === JobStatus.Unprocessed;

const JobStatusCell = ({ value }: { value: IImportJobSummaryWithId }) => (
  <ImportStatusChip status={value.jobStatusName} />
);

const ResultsCell = ({ value }: { value: IImportJobSummaryWithId }) => (
  <ImportsResultsCell counts={value.resultCounts} />
);

type QuickFilter = "all" | "processing" | "succeeded" | "hasErrors";

/**
 * "Has errors" spans two statuses, which is why the filter travels as a list of ids rather than
 * the single JobStatusId the dropdown uses.
 */
const quickFilterStatusNames: Record<QuickFilter, string[]> = {
  all: [],
  processing: [JobStatus.Processing, JobStatus.Unprocessed],
  succeeded: [JobStatus.Processed],
  hasErrors: [JobStatus.Error, JobStatus.ProcessedWithErrors],
};

const quickFilterLabelKeys: Record<QuickFilter, string> = {
  all: "quickFilterAllLabel",
  processing: "quickFilterProcessingLabel",
  succeeded: "quickFilterSucceededLabel",
  hasErrors: "quickFilterHasErrorsLabel",
};

const isQuickFilter = (value?: string): value is QuickFilter =>
  !!value && value in quickFilterStatusNames;

export const ImportsStatusPage = () => {
  const { formatMessage: t } = useIntl();
  const { organizationId } = contextStore.useState();

  const {
    migrationJobService,
    importsUploadUrl,
    importsSummaryUrl,
  } = useGlobalContext();

  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("createdOn");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [summary, setSummary] = useState<IImportJobSummaryWithId[]>([]);

  const importJobStartDateSearch = persistanceStore.useState(
    (s) => s.importJobEventStartDate
  );
  const importJobEndDateSearch = persistanceStore.useState(
    (s) => s.importJobEventEndDate
  );

  const jobTypeId = persistanceStore.useState((s) => s.jobTypeId);
  const jobStatusId = persistanceStore.useState((s) => s.jobStatusId);
  const importSourceTypeId = persistanceStore.useState(
    (s) => s.importSourceTypeId
  );
  const storedQuickFilter = persistanceStore.useState(
    (s) => s.importJobQuickFilter
  );
  const quickFilter = isQuickFilter(storedQuickFilter)
    ? storedQuickFilter
    : "all";

  const [appliedQuickFilter, setAppliedQuickFilter] = useState(quickFilter);

  if (appliedQuickFilter !== quickFilter) {
    setAppliedQuickFilter(quickFilter);
    setPage(0);
  }

  useListTitle("importsLabel");

  const importJobStatusResponse = useQuery<ListResponseOfImportJobStatus>(
    "importJobStatus",
    () =>
      migrationJobService.getJobStatuses(
        new ListRequestOfBaseFilter({ sortField: "name" })
      )
  );

  const importSourceTypesResponse = useQuery<ListResponseOfIdNameModel>(
    "importSourceTypes",
    () => migrationJobService.getImportSourceTypes()
  );

  const importJobTypesResponse = useQuery<ListResponseOfImportJobType>(
    "importJobTypes",
    () =>
      migrationJobService.getImportJobTypes(
        new ListRequestOfJobTypeFilter({
          parameters: JobTypeFilter.fromJS({ onlyCsvConfigured: false }),
        })
      )
  );

  // An empty list reads as "no filter" on the server, so a chip must never resolve to one: until the status
  // lookup lands the whole query waits, and a name that matches nothing narrows to a sentinel instead of widening.
  const jobStatusIds = useMemo(() => {
    const names = quickFilterStatusNames[quickFilter];

    if (names.length === 0) {
      return undefined;
    }

    const matched = (importJobStatusResponse.data?.data ?? [])
      .filter((status) => names.includes(status.name!))
      .map((status) => status.id!);

    return matched.length > 0 ? matched : [EMPTY_GUID];
  }, [quickFilter, importJobStatusResponse.data]);

  const isQuickFilterReady =
    quickFilter === "all" || importJobStatusResponse.isSuccess;

  const {
    isError,
    error,
    data,
    isFetching,
    refetch,
  } = useQuery<ListResponseOfImportJobSummary>(
    [
      "importJobSummaries",
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      jobStatusId,
      jobTypeId,
      importSourceTypeId,
      importJobStartDateSearch,
      importJobEndDateSearch,
      jobStatusIds,
    ],
    () =>
      migrationJobService.getImportJobSummaries(
        AdminListRequestOfJobSummaryFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: JobSummaryFilter.fromJS({
            jobStatusId,
            jobTypeId,
            importSourceTypeId,
            jobStatusIds,
            startDate: importJobStartDateSearch,
            endDate: importJobEndDateSearch,
          }),
        })
      ),
    {
      enabled: isQuickFilterReady,
      // The app sets staleTime: Infinity globally, so returning here after creating an import would otherwise
      // serve the cached page and never show the new job — polling alone does not cover it, because it stops
      // once nothing on the page is in progress.
      refetchOnMount: "always",
      // react-query drops an interval tick while the window is unfocused, and refetchOnWindowFocus is off
      // app-wide, so without this the Results column stops advancing as soon as you switch away.
      refetchIntervalInBackground: true,
      refetchInterval: (latest) =>
        (latest?.data ?? []).some(isInProgress) ? POLL_INTERVAL_MS : false,
    }
  );

  useEffect(() => {
    setPage(0);
    refetch();
  }, [
    refetch,
    organizationId,
    jobStatusId,
    jobTypeId,
    importSourceTypeId,
    importJobStartDateSearch,
    importJobEndDateSearch,
  ]);

  useEffect(() => {
    const rawSummary = (data?.data ?? []).map((row: ImportJobSummary) => ({
      ...row,
      id: row.id!,
      isExpandable: false,
      renderExpandedData: () => {},
    }));

    setSummary(rawSummary);
  }, [data?.data]);

  const cells: Array<TableCellType> = [
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "organization" }),
    },
    {
      accessor: "jobName",
      customSortAccessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "createdOn",
      align: "left",
      width: 120,
      type: "text",
      label: t({ id: "uploadDate" }),
    },
    {
      accessor: "jobTypeName",
      customSortAccessor: "jobType.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "jobTypeLabel" }),
    },
    {
      accessor: "jobStatusName",
      customSortAccessor: "jobStatus.name",
      align: "left",
      width: 0,
      type: "node",
      View: JobStatusCell,
      label: t({ id: "statusLabel" }),
    },
    {
      accessor: "importJobSourceTypeName",
      customSortAccessor: "importSourceType.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "sourceLabel" }),
    },
    {
      accessor: "resultCounts",
      align: "left",
      width: 0,
      type: "node",
      View: ResultsCell,
      label: t({ id: "resultsLabel" }),
      noSort: true,
    },
  ];

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const onClick = (id: any) => {
    navigate(importsSummaryUrl(id));
  };

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Link to={importsUploadUrl()} style={{ textDecoration: "none" }}>
          <Fab aria-label="add" color="primary" data-cy="addButton">
            <AddIcon />
          </Fab>
        </Link>
      </Box>

      <Box mb={2} sx={styles.quickFilters}>
        {(Object.keys(quickFilterLabelKeys) as QuickFilter[]).map((key) => (
          <Chip
            key={key}
            label={t({ id: quickFilterLabelKeys[key] })}
            size="small"
            color={key === "hasErrors" ? "error" : "primary"}
            variant={quickFilter === key ? "filled" : "outlined"}
            onClick={() =>
              persistanceStore.update((s) => {
                s.importJobQuickFilter = key;
                // The dropdown and the chips are separate server-side predicates that would otherwise AND
                // into an unexplained empty list.
                s.jobStatusId = undefined;
              })
            }
          />
        ))}
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6}>
            <SelectEntity
              value={jobTypeId || ""}
              fetchedData={importJobTypesResponse}
              labelField="name"
              label={t({ id: "jobTypeLabel" })}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.jobTypeId = id;
                })
              }
              textFieldProps={{
                variant: "outlined",
              }}
              data-cy="jobTypeLabelFilter"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <SelectEntity
              value={importSourceTypeId || ""}
              fetchedData={importSourceTypesResponse}
              labelField="name"
              label={t({ id: "sourceLabel" })}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.importSourceTypeId = id;
                })
              }
              textFieldProps={{
                variant: "outlined",
              }}
              data-cy="sourceLabelFilter"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <SelectEntity
              value={jobStatusId || ""}
              fetchedData={importJobStatusResponse}
              labelField="name"
              label={t({ id: "jobStatusLabel" })}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.jobStatusId = id;
                })
              }
              textFieldProps={{
                variant: "outlined",
              }}
              data-cy="jobStatusFilter"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <DatePicker
              label={t({ id: "startDateLabel" })}
              disabled={isFetching}
              value={importJobStartDateSearch || null}
              onChange={(date: any) =>
                persistanceStore.update((s) => {
                  if (isValid(date)) {
                    s.importJobEventStartDate = date;
                  } else if (date == null) {
                    s.importJobEventStartDate = undefined;
                  }
                })
              }
              format={standardDateFormat}
              data-cy="eventStartDateFilter"
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: {
                  actions: ["clear", "cancel"],
                },
              }}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <DatePicker
              label={t({ id: "endDateLabel" })}
              disabled={isFetching}
              value={importJobEndDateSearch || null}
              onChange={(date: any) =>
                persistanceStore.update((s) => {
                  if (isValid(date)) {
                    s.importJobEventEndDate = date;
                  } else if (date == null) {
                    s.importJobEventEndDate = undefined;
                  }
                })
              }
              format={standardDateFormat}
              data-cy="eventEndDateFilter"
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: {
                  actions: ["clear", "cancel"],
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: summary ?? [],
          loading: isFetching,
          titleComponent: (
            <Box m={2}>
              <Grid
                container
                justifyItems="center"
                justifyContent="space-between"
              >
                <Grid item xs={12} sm={10} md={10}>
                  <Typography variant="h6">
                    <T id="importsLabel" />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                  <Tooltip
                    title={t({
                      id: "refresh",
                    })}
                  >
                    <IconButton onClick={() => refetch()}>
                      <CachedIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
          ),
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => importsSummaryUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

